import { Box, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getColors, shadows } from "./bookings.styles";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

const BookingAttendanceTrend = ({ data }) => {
  const theme = useTheme();
  const colors = getColors(theme);
  const primaryColor = theme.palette.primary.main;

  // Use data from API or empty array
  const chartData = Array.isArray(data) ? data : [];

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    const bookingsData = payload.find(p => p.dataKey === "bookings");
    const completedData = payload.find(p => p.dataKey === "completed");
    const showRateData = payload.find(p => p.dataKey === "showRate");

    return (
      <Box
        sx={{
          bgcolor: "#FFFFFF",
          px: 2.5,
          py: 2,
          borderRadius: 2,
          boxShadow: "0 8px 24px rgba(0,0,0,.15)",
          border: `1px solid ${colors.borderLight}`,
          minWidth: 180,
        }}
      >
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            color: colors.textMuted,
            letterSpacing: 0.5,
            mb: 1.5,
          }}
        >
          METRICS: {label}, 2026
        </Typography>

        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
              Total Bookings
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>
              {bookingsData?.value || 0}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
              Completed
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: primaryColor }}>
              {completedData?.value || 0}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
              Completion Rate
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: colors.success }}>
              {showRateData?.value || 0}%
            </Typography>
          </Stack>
        </Stack>
      </Box>
    );
  };

  // Show empty state if no data
  if (!chartData || chartData.length === 0) {
    return (
      <Box
        sx={{
          bgcolor: "#FFFFFF",
          borderRadius: 3,
          p: 3,
          border: `1px solid ${colors.borderLight}`,
          boxShadow: shadows.card,
          flex: 1,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 350,
        }}
      >
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 700,
            color: colors.textPrimary,
            mb: 1,
          }}
        >
          Daily Booking Trend
        </Typography>
        <Typography
          sx={{
            fontSize: 14,
            color: colors.textSecondary,
          }}
        >
          No data available for the selected period
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: 3,
        p: 3,
        border: `1px solid ${colors.borderLight}`,
        boxShadow: shadows.card,
        flex: 1,
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={2}>
        <Box>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 700,
              color: colors.textPrimary,
              mb: 0.5,
            }}
          >
            Daily Booking Trend
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              color: colors.textSecondary,
            }}
          >
            Comparison of daily bookings vs. completed appointments
          </Typography>
        </Box>

        {/* Legend */}
        <Stack direction="row" spacing={3}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 16,
                height: 2,
                bgcolor: primaryColor,
                borderRadius: 1,
              }}
            />
            <Typography fontSize={11} fontWeight={500} color={colors.textSecondary}>
              Completed
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      {/* Chart */}
      <Box sx={{ height: 280 }}>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart
            data={chartData}
            margin={{ top: 20, right: 20, left: 0, bottom: 5 }}
          >
            <defs>
              <linearGradient id="bookingBarGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={primaryColor} stopOpacity={0.9} />
                <stop offset="100%" stopColor={primaryColor} stopOpacity={0.7} />
              </linearGradient>
            </defs>

            <CartesianGrid
              vertical={false}
              stroke={colors.borderLight}
              strokeDasharray="4 4"
            />

            <XAxis
              dataKey="date"
              tick={{ fill: colors.textMuted, fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              dy={10}
            />

            <YAxis
              tick={{ fill: colors.textMuted, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(value) => value >= 1000 ? `${value / 1000}k` : value}
              dx={-10}
              width={40}
            />

            <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />

            <Bar
              dataKey="bookings"
              fill="url(#bookingBarGradient)"
              radius={[4, 4, 0, 0]}
              barSize={40}
            />

            <Line
              type="monotone"
              dataKey="completed"
              stroke={primaryColor}
              strokeWidth={2}
              dot={{
                r: 5,
                fill: "#fff",
                stroke: primaryColor,
                strokeWidth: 2,
              }}
              activeDot={{
                r: 7,
                fill: primaryColor,
                stroke: "#fff",
                strokeWidth: 3,
              }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default BookingAttendanceTrend;
